import { FunctionComponent, useMemo, type ReactElement } from 'react';
import type React from 'react';

import { useLatestRef } from '@/domains/core/data/hooks/useLatestRef';
import { Playlist } from '@/domains/core/tamagoshiTailwind/components/Playlist/Playlist';
import type { Product } from '@/domains/productDiscovery/HeaderLeonidas/modules/productSuggestions/interfaces/product';

import { ProductNotFound } from '../ProductNotFound/ProductNotFound';
import { AlgoliaMiniProductCard } from './components/AlgoliaMiniProductCard/AlgoliaMiniProductCard';
import { ProductsContainer } from './components/ProductsContainer/ProductsContainer';
import { ariaLabelNext, ariaLabelPrevious } from './translations';

interface Props {
  isDesktop: boolean;
  values: Product[];
  button?: ReactElement<{ className: string }>;
  onClickProductCard?: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    product: Product,
    position: number,
  ) => void;
  title?: string | ReactElement<{ className: string }>;
}

export const ProductsList: FunctionComponent<Props> = ({
  isDesktop,
  values,
  button,
  onClickProductCard,
  title,
}) => {
  const latestOnClickProductCard = useLatestRef(onClickProductCard);
  const productCards = useMemo(
    () =>
      values.map((product, index) => (
        <AlgoliaMiniProductCard
          key={product.productId}
          product={product}
          imageLoading="lazy"
          vertical
          onClick={(event, productClicked) =>
            latestOnClickProductCard.current?.(event, productClicked, index)
          }
        />
      )),
    [latestOnClickProductCard, values],
  );

  if (!productCards.length) {
    return <ProductNotFound />;
  }

  if (isDesktop) {
    return (
      <ProductsContainer
        productsElement={productCards}
        title={title}
        button={button}
      />
    );
  }

  return (
    <>
      <div>
        {title}
        <Playlist
          controlTitleLeft={ariaLabelPrevious}
          controlTitleRight={ariaLabelNext}
        >
          {productCards}
        </Playlist>
      </div>
      {button && (
        <div
          className={
            'fixed bottom-0 left-0 w-full bg-foundation-inverted px-m py-l'
          }
        >
          {button}
        </div>
      )}
    </>
  );
};

ProductsList.displayName = 'ProductsList';
